/**
 * Состояние push-уведомлений на этом устройстве.
 *
 * Ключевая мысль: Notification.permission === 'granted' НЕ означает,
 * что подписка существует. Клиент мог почистить данные сайта или
 * переустановить PWA — разрешение останется, а подписки не будет,
 * и уведомления перестанут приходить незаметно для него.
 * Поэтому спрашиваем сам Service Worker.
 */

const SNOOZE_KEY = 'client_push_prompt_snoozed_until';
export const SNOOZE_LATER_MS = 24 * 60 * 60 * 1000;      // «Позже» — сутки
export const SNOOZE_CLOSE_MS = 3 * 24 * 60 * 60 * 1000;  // крестик — трое суток

export const isPromptSnoozed = () => {
    try {
        const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
        return until > Date.now();
    } catch {
        return false;
    }
};

export const snoozePrompt = (ms = SNOOZE_LATER_MS) => {
    try {
        localStorage.setItem(SNOOZE_KEY, String(Date.now() + ms));
    } catch {
        // приватный режим — не критично, просто покажем предложение снова
    }
};

export const resetPromptSnooze = () => {
    try { localStorage.removeItem(SNOOZE_KEY); } catch {}
};

export const isPushSupported = () =>
    typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;

export const isStandalonePWA = () =>
    window.navigator?.standalone === true
    || window.matchMedia?.('(display-mode: standalone)')?.matches === true;

export const isIOS = () => {
    const ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua)
        || (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua));
};

/** Текущая подписка этого устройства или null */
export const getLocalSubscription = async () => {
    if (!isPushSupported()) return null;
    try {
        const registration = await navigator.serviceWorker.ready;
        return await registration.pushManager.getSubscription();
    } catch (e) {
        console.warn('[push] getSubscription:', e);
        return null;
    }
};

/**
 * Полное состояние push для этого устройства.
 *   supported     — браузер умеет push
 *   permission    — 'default' | 'granted' | 'denied'
 *   subscribed    — подписка реально существует в SW
 *   needsPrompt   — есть смысл предложить подключить
 *   variant       — что именно показать: enable | ios_install | denied
 */
export const getPushState = async () => {
    const supported = isPushSupported();
    if (!supported) {
        return { supported: false, permission: 'unsupported', subscribed: false, needsPrompt: false, variant: null };
    }

    const permission = Notification.permission;
    const subscription = await getLocalSubscription();
    const subscribed = !!subscription;

    if (permission === 'denied') {
        return { supported, permission, subscribed: false, needsPrompt: true, variant: 'denied' };
    }

    // iOS отдаёт push только установленному на домашний экран приложению
    if (permission === 'default' && isIOS() && !isStandalonePWA()) {
        return { supported, permission, subscribed, needsPrompt: true, variant: 'ios_install' };
    }

    if (permission === 'granted' && subscribed) {
        return { supported, permission, subscribed: true, needsPrompt: false, variant: null };
    }

    // granted без подписки — тоже повод предложить: уведомления не доходят
    return { supported, permission, subscribed, needsPrompt: true, variant: 'enable' };
};